import { Button, Modal } from 'react-bootstrap';
import { RunGetDriverEstimatedDriveTimeInput, Trip, TripTableFormatEnum } from '@/models/gen/graphql';
import { Dayts, getClasses, handleError, queryInput, titleCase } from '@/utils';
import { useCallback, useEffect, useState } from 'react';
import useTripTableState from '@/features/Trips/components/TripsTable/hook';

import { AssignDriverDropdown } from '@/components/DriverDropdown';
import HasPermission from '@/components/HasPermission';
import { ILLEGAL_COMBINES } from '@/constants';
import ImageDisplay from '@/components/ImageDisplay';
import LoadingSpinner from '@/components/LoadingSpinner';
import React from 'react';
import SimpleTableCell from '@/components/SimpleTable/SimpleTableCell';
import Tooltip from '@/components/Tooltip';
import { formatTripTitle } from '@/features/Trips/components/TripsTable/utils';
import { refetchSearchTripsTable } from '@/api/services/trips/searchTrips';
import runAssignDriver from '@/api/services/trips/runAssignDriver';
import runGetDriverEstimatedDriveTime from '@/api/services/trips/runGetDriverEstimatedDriveTime';
import runUnassignDriver from '@/api/services/trips/runUnassignDriver';
import { useGetDriverEtaWithPreviousTrip } from '@/api/services/trips/getDriverEtaWithPreviousTrip';
import useConfirmation from '@/hooks/useConfirmation';
import useDelayCallback from '@/hooks/useDelayCallback';
import { useSimpleTableRowContext } from '@/components/SimpleTable/SimpleTableRow';

const AssignDriverCell = (): React.JSX.Element => {
  const onSetRow = useTripTableState(({ state }) => state.onSetRow);
  const {
    data: {
      id,
      airportCode,
      trackFlight,
      driver,
      puLocationId,
      combineType,
      scheduled,
      servicerIataAirlineCode,
      flightNumber,
      driverEtaId,
      driverEtaMinutes,
    } = {},
  } = useSimpleTableRowContext<Trip>();
  const { actual } = trackFlight || {};
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [value, setValue] = useState<string>('');
  const [driveTimeWarning, setDriveTimeWarning] = useState<string>('');
  const title = formatTripTitle(servicerIataAirlineCode, flightNumber, scheduled);

  const [, { fetch: getDriverEtaWithPreviousTrip }] = useGetDriverEtaWithPreviousTrip();

  // Always fetch the previous-trip detail on hover so the tooltip reflects live data --
  // reuses driveTimeWarning as the single source of truth for the tooltip text; only fill
  // it in if a fresh live check (handleDriveTimeEstimate) hasn't already set it.
  const [handleWarningMouseEnter, handleWarningMouseLeave] = useDelayCallback((): void => {
    getDriverEtaWithPreviousTrip(driverEtaId)
      .then((previousTripDetail) => {
        if (!previousTripDetail) return;
        // matches the backend's runGetDriverEstimatedDriveTime warning format (trip-api
        // service.go: `fmt.Sprintf("%s%d %s will take %d mins to complete and get to this pickup", ...)`)
        const message = `${previousTripDetail.previousTripServicerIataAirlineCode}${previousTripDetail.previousTripFlightNumber} ${new Dayts(
          previousTripDetail.previousTripScheduled
        ).formatAs(Dayts.Format.TIME)} will take ${previousTripDetail.etaMinutes} mins to complete and get to this pickup`;
        setDriveTimeWarning((current) => current || message);
      })
      .catch((err) => {
        handleError(err, { notification: { title: 'Get Driver Eta With Previous Trip' } });
      });
  }, 250);

  const confirmIllegalCombines = useConfirmation({
    Header: {
      as: ({ data }) => (
        <div className="d-flex w-100 justify-content-start align-items-center gap-2 border-bottom border-1 pb-3 fs-3 mb-4">
          <i className="sv sv-warning text-danger" />
          <div>ILLEGAL COMBINE - {data?.title}</div>
        </div>
      ),
    },
    Footer: {
      as: ({ onResolve, onReject }) => (
        <Modal.Footer className="d-flex justify-content-end mt-3">
          <div>
            <Button name="REJECT" className="flex-grow-1 px-4" variant="secondary" onClick={onReject}>
              CANCEL
            </Button>
          </div>
          <div>
            <Button name="RESOLVE" className="flex-grow-1 px-4" variant="danger" onClick={onResolve}>
              DO IT ANYWAY
            </Button>
          </div>
        </Modal.Footer>
      ),
    },
  });

  const handleDriveTimeEstimate = useCallback((input: RunGetDriverEstimatedDriveTimeInput) => {
    // check if driver estimated drive time
    if (!HasPermission.check('allowGetDriverEstimatedDriveTime')) return;
    setLoading(true);
    // clear drive time warning
    setDriveTimeWarning('');
    // bad scheduling flow
    runGetDriverEstimatedDriveTime(input)
      .then((res) => {
        if (!res?.warning) return res;
        setDriveTimeWarning(res.warning);
      })
      .catch((err) => {
        handleError(err, { notification: { title: 'Get Driver Estimated Drive Time' } });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Refetch the target plus every trip it was combined with, so driver, vehicle, persisted
  // combine flag, and drive-time fields all stay in sync (rather than hand-assembling a patch).
  const syncRowsAfterAssign = useCallback(
    async (combinedTripIds: string[] = []): Promise<void> => {
      const ids = Array.from(new Set([id, ...combinedTripIds].filter(Boolean)));
      const { rows } = await refetchSearchTripsTable(
        { format: TripTableFormatEnum.All, query: [{ id: queryInput(ids) }] },
        { pageSize: ids.length }
      );
      (rows || []).forEach((freshTrip: Trip): void => {
        if (freshTrip?.id) onSetRow(freshTrip, [freshTrip.id]);
      });
      const target = (rows || []).find((row: Trip): boolean => row.id === id);
      handleDriveTimeEstimate({ scheduled, toLocationId: puLocationId, tripId: id, driverId: target?.driver?.id });
    },
    [id, scheduled, puLocationId, onSetRow, handleDriveTimeEstimate]
  );

  // The backend owns illegal-combine detection/persistence. An unapproved assignment that
  // would create an illegal combine writes nothing and returns illegalCombine.required, so
  // we prompt the dispatcher and, on approval, re-call the same endpoint. The frontend never
  // decides legality or persists the combine itself.
  const assignDriver = useCallback(
    async (driverId: string): Promise<void> => {
      let res = await runAssignDriver({ tripId: id, driverId, scheduled, illegalCombineApproved: false });

      if (res?.illegalCombine?.required) {
        const trips = (res.illegalCombine.trips || []) as Trip[];
        const conflictDriver = trips?.[0]?.driver;
        try {
          await confirmIllegalCombines(
            {
              Body: {
                as: () => (
                  <IllegalCombineMessage trips={trips} fullname={conflictDriver?.fullName} employeeId={conflictDriver?.employeeId} />
                ),
              },
            },
            { title }
          );
        } catch {
          // CANCEL -> backend persisted nothing; restore the dropdown to the current driver.
          setValue(driver?.id || '');
          return;
        }
        // DO IT ANYWAY -> re-call with approval; backend assigns + persists atomically.
        res = await runAssignDriver({ tripId: id, driverId, scheduled, illegalCombineApproved: true });
      }

      // Sync the target and every trip it was combined with.
      await syncRowsAfterAssign((res?.illegalCombine?.trips || []).map((trip: Trip) => trip.id));
    },
    [id, scheduled, driver?.id, confirmIllegalCombines, title, syncRowsAfterAssign]
  );

  const handleChange = useCallback(
    async (input: string): Promise<void> => {
      try {
        setLoading(true);
        if (!!input && input !== driver?.id) {
          setValue(input);
          await assignDriver(input);
        } else if (input) {
          console.debug('unassigning driver');
          await runUnassignDriver(id);
          onSetRow(
            {
              driverId: null,
              driver: null,
              vehicleId: null,
              vehicle: null,
              combineType: null,
              combineId: null,
              driverEtaId: null,
              driverEtaMinutes: null,
              id,
            },
            null
          );
          setValue('');
          setDriveTimeWarning('');
        }
      } catch (err) {
        setValue(driver?.id);
        handleError(err, {
          notification: { title: !!input && input !== driver?.id ? 'Assign Driver' : 'Unassign Driver' },
        });
      } finally {
        setLoading(false);
      }
    },
    [id, driver?.id, onSetRow, assignDriver]
  );

  useEffect(() => {
    if (value === (driver?.id || '')) return;
    setValue(driver?.id || '');
  }, [driver?.id, value]);

  const isIllegalCombine = ILLEGAL_COMBINES.includes(combineType);
  const isDriveTimeWarning = !!driveTimeWarning || !!driverEtaId;
  const hasWarning = isDriveTimeWarning || isIllegalCombine;
  const display = (driver?.fullName && `${driver?.employeeId || ''} ${driver?.fullName}`.trim()) || (loading ? '' : '--');

  return (
    <SimpleTableCell name="DRIVER" className={`w-md-grow ${!driver ? 'UNASSIGNED' : ''}`}>
      <div className={`h-100 ${!loading ? 'w-100' : ''}`}>
        {editing && (
          <AssignDriverDropdown
            name="driver"
            className="DropdownCell h-100 {padding-left:0;padding-right:0!;text-align:left;}>input"
            value={value || ''}
            airportCode={airportCode}
            scheduled={scheduled}
            actual={actual}
            tripId={id}
            details={driver}
            onChange={(val) => {
              handleChange(val);
              setEditing(false);
            }}
            autoFocus
            options={{
              onToggle: setEditing,
              loading,
              showLoadingSpinner: false,
              disabled: loading,
              lazyLoadItems: true,
              openOnMount: true,
              showRefreshButton: false,
              showChevron: false,
              showClearButton: false,
              locale: { 'Select...': display },
              autoSelect: false,
            }}
          />
        )}
        {!editing && (
          <Tooltip
            className="w-100 h-100"
            placement="left"
            content={
              driver && (
                <div className="d-flex">
                  <ImageDisplay className="{width:50px!;height:50px!;margin-right:.7rem;}" src={driver?.avatar} />
                  <div>
                    <div>{driver?.fullName || '--'}</div>
                    <div>{driver?.employeeId || '--'}</div>
                    <div>{(driver?.cityName || '--').toUpperCase()}</div>
                  </div>
                </div>
              )
            }
          >
            <Button variant="icon" className="w-100 h-100 p-0 {text-align:left!;}" onClick={(): void => setEditing(true)}>
              {display}
            </Button>
          </Tooltip>
        )}
      </div>
      {loading && (
        <span className="ms-1">
          <LoadingSpinner size="sm" />
        </span>
      )}
      {!loading && hasWarning && (
        <Tooltip
          content={
            <div className="d-flex flex-column gap-1 p-2 text-center">
              <div className="text-danger fs-6">WARNING:</div>
              <div>{isIllegalCombine && 'This combine is against contract and policy!'}</div>
              {!!driveTimeWarning && <div className="fw-bold">{titleCase(driveTimeWarning)}</div>}
              {!driveTimeWarning && !!driverEtaId && (
                <div className="fw-bold">{`Driver last trip will take ${driverEtaMinutes} mins to complete and get to this pickup`}</div>
              )}
            </div>
          }
        >
          <i
            className={getClasses(
              'sv fs-5 text-danger px-1',
              isDriveTimeWarning ? 'sv-timer' : undefined,
              isIllegalCombine ? 'sv-warning' : undefined
            )}
            onMouseEnter={handleWarningMouseEnter}
            onMouseLeave={handleWarningMouseLeave}
          />
        </Tooltip>
      )}
    </SimpleTableCell>
  );
};

const IllegalCombineMessage = (props: { trips: Trip[]; fullname: string; employeeId: string }): React.JSX.Element => (
  <>
    <div className="text-center">
      This combine is against contract and policy!
      <br />
      Driver<strong>{` ${props.employeeId} ${props.fullname} `}</strong>already has the following pickups:
    </div>
    <div className="overflow-auto pt-2 {max-height:15rem}">
      {props.trips.map(
        (trip: Trip, index: number): React.JSX.Element => (
          <div className="mb-2 text-center" key={index}>
            <strong>
              {`${trip?.puLocation?.name} - `}
              {formatTripTitle(trip.servicerIataAirlineCode, trip.flightNumber, trip.scheduled)}
            </strong>
          </div>
        )
      )}
    </div>
  </>
);

export default React.memo(AssignDriverCell);
